// The images a tool call gave back (SUN-1331), under its row: a file it read,
// a screenshot it took. Moved from today's Code tab (code/ItemImages.tsx
// there). Today's server kept an image apart and the page asked for it; ACP
// brings its bytes in the update itself, base64, so each is drawn from them,
// as a raster image alone: a type that is not one is named, not drawn.
//
// Each is drawn small, and whole on a click.
import { useState } from 'react'
import { cx } from './bits'
import type { ImageData } from './types'

/** A size as a reader says it: `812 B`, `99 kB`, `1.2 MB`. */
export function bytesText(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return ''
  if (bytes < 1000) return `${Math.round(bytes)} B`
  if (bytes < 1_000_000) return `${Math.round(bytes / 1000)} kB`
  return `${(bytes / 1_000_000).toFixed(1)} MB`
}

/** The types an image is drawn in: rasters, never SVG, which is a document. */
const DRAWN = new Set(['image/png', 'image/jpeg', 'image/gif', 'image/webp'])

const BASE64 = /^[A-Za-z0-9+/]*={0,2}$/

/** How many bytes a base64 text holds. */
export function base64Bytes(data: string): number {
  const padding = data.endsWith('==') ? 2 : data.endsWith('=') ? 1 : 0
  return Math.max(0, Math.floor((data.length * 3) / 4) - padding)
}

/** The page's own URL for an image: its bytes, when they are a raster image's base64; null otherwise. */
export function imageUrl(image: ImageData): string | null {
  const mime = image.mime.toLowerCase()
  return DRAWN.has(mime) && image.data !== '' && BASE64.test(image.data) ? `data:${mime};base64,${image.data}` : null
}

function SessionImage({ image, onFold }: { image: ImageData; onFold: () => void }) {
  const [whole, setWhole] = useState(false)
  const url = imageUrl(image)
  const size = bytesText(base64Bytes(image.data))
  if (url === null) {
    return (
      <span className="code-image-state" role="note">
        An image{image.mime ? ` (${image.mime}${size ? `, ${size}` : ''})` : size ? ` (${size})` : ''}
      </span>
    )
  }
  return (
    <button
      type="button"
      className={cx('code-image', whole && 'is-open')}
      aria-expanded={whole}
      aria-label={`Image${size ? `, ${size}` : ''}: ${whole ? 'show it smaller' : 'show it whole'}`}
      onClick={() => {
        onFold()
        setWhole((was) => !was)
      }}
    >
      <img src={url} alt="" />
    </button>
  )
}

/** The images of one item; nothing for an item that has none. */
export function ItemImages({ images, onFold }: { images: readonly ImageData[] | undefined; onFold: () => void }) {
  if (!images || images.length === 0) return null
  return (
    <div className="code-images" role="group" aria-label={images.length === 1 ? 'Image' : `${images.length} images`}>
      {images.map((image, at) => (
        <SessionImage key={at} image={image} onFold={onFold} />
      ))}
    </div>
  )
}
